import { useEffect, useState } from 'react'

export interface VisualViewportSize {
  width: number
  height: number
  offsetTop: number
}

function readInitial(): VisualViewportSize {
  if (typeof window === 'undefined') {
    return { width: 1024, height: 768, offsetTop: 0 }
  }
  const vv = window.visualViewport
  if (vv) {
    return { width: vv.width, height: vv.height, offsetTop: vv.offsetTop }
  }
  return {
    width: window.innerWidth || 1024,
    height: window.innerHeight || 768,
    offsetTop: 0,
  }
}

/**
 * Returns the current visual viewport size (width / height / offsetTop).
 * Uses `window.visualViewport` when available — necessary on iOS where the
 * on-screen keyboard shrinks the visual viewport without changing
 * `window.innerHeight`. Falls back to `window.innerWidth/innerHeight` when
 * the API is unavailable.
 */
export function useVisualViewport(): VisualViewportSize {
  const [size, setSize] = useState<VisualViewportSize>(readInitial)

  useEffect(() => {
    if (typeof window === 'undefined') return
    const vv = window.visualViewport
    if (!vv) return
    const onChange = (): void =>
      setSize({ width: vv.width, height: vv.height, offsetTop: vv.offsetTop })
    vv.addEventListener('resize', onChange)
    vv.addEventListener('scroll', onChange)
    return () => {
      vv.removeEventListener('resize', onChange)
      vv.removeEventListener('scroll', onChange)
    }
  }, [])

  return size
}
